import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Cloud, Wind, Droplets, Thermometer, CloudRain, Sun, CloudSnow, AlertCircle } from 'lucide-react';

const API = window.__BACKEND_URL__ || 'http://localhost:8001';

const WeatherWidget = ({ latitude, longitude }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (latitude && longitude) {
      fetchWeather().catch(err => {
        console.error('Weather widget error:', err);
        setError('Weather unavailable');
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [latitude, longitude]);

  const fetchWeather = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token');
      }
      
      const response = await axios.get(`${API}/api/weather/forecast`, {
        params: { lat: latitude, lon: longitude },
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      
      if (response.data) {
        setWeather(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Weather fetch error:', err);
      setError(err.response?.data?.detail || err.message || 'Weather service unavailable');
      setLoading(false);
      // Don't throw - just set error state
    }
  };

  const getWeatherIcon = (iconCode) => {
    const iconMap = {
      '01d': <Sun className="w-12 h-12 text-yellow-400" />,
      '01n': <Sun className="w-12 h-12 text-gray-400" />,
      '02d': <Cloud className="w-12 h-12 text-gray-400" />,
      '02n': <Cloud className="w-12 h-12 text-slate-500" />,
      '03d': <Cloud className="w-12 h-12 text-gray-400" />,
      '03n': <Cloud className="w-12 h-12 text-slate-500" />,
      '04d': <Cloud className="w-12 h-12 text-slate-500" />,
      '04n': <Cloud className="w-12 h-12 text-slate-400" />,
      '09d': <CloudRain className="w-12 h-12 text-blue-400" />,
      '09n': <CloudRain className="w-12 h-12 text-blue-500" />,
      '10d': <CloudRain className="w-12 h-12 text-blue-400" />,
      '10n': <CloudRain className="w-12 h-12 text-blue-500" />,
      '11d': <CloudRain className="w-12 h-12 text-purple-500" />,
      '11n': <CloudRain className="w-12 h-12 text-purple-600" />,
      '13d': <CloudSnow className="w-12 h-12 text-blue-200" />,
      '13n': <CloudSnow className="w-12 h-12 text-blue-300" />,
    };
    return iconMap[iconCode] || <Cloud className="w-12 h-12 text-gray-400" />;
  };

  const celsiusToFahrenheit = (celsius) => {
    return Math.round((celsius * 9/5) + 32);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Don't render anything if no location - fail silently
  if (!latitude || !longitude) {
    return null;
  }

  // If there's a critical error, fail silently (don't crash the page)
  if (error && error.includes('unavailable')) {
    return null; // Hide widget if service is down
  }

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
          <CardTitle className="flex items-center space-x-2">
            <Cloud className="w-6 h-6" />
            <span>Weather Forecast</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="ml-3 text-slate-400">Loading weather...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-lg border-yellow-200">
        <CardHeader className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white">
          <CardTitle className="flex items-center space-x-2">
            <AlertCircle className="w-6 h-6" />
            <span>Weather Unavailable</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-slate-400 text-sm">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!weather) return null;

  const { current, forecast } = weather;

  return (
    <Card className="shadow-xl border-2 border-blue-200">
      <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-t-lg">
        <CardTitle className="flex items-center space-x-2 text-xl">
          <Cloud className="w-7 h-7" />
          <span>Today's Weather</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {/* Current Weather */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {getWeatherIcon(current.icon)}
              <div>
                <div className="text-5xl font-bold text-white">
                  {celsiusToFahrenheit(current.temp)}°F
                </div>
                <div className="text-sm text-slate-400 capitalize mt-1">
                  {current.description}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Feels like {celsiusToFahrenheit(current.feels_like)}°F
                </div>
              </div>
            </div>
            <div className="text-right space-y-2">
              <div className="flex items-center justify-end space-x-2">
                <Wind className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-300">{current.wind_speed} m/s</span>
              </div>
              <div className="flex items-center justify-end space-x-2">
                <Droplets className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-slate-300">{current.humidity}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* 5-Day Forecast */}
        <div>
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
            <Thermometer className="w-5 h-5 mr-2 text-blue-600" />
            5-Day Forecast
          </h3>
          <div className="grid grid-cols-5 gap-2">
            {forecast.map((day, index) => (
              <div
                key={day.date || day.dt || `forecast-${index}`}
                className="bg-[#1A2433] border-2 border-gray-100 rounded-lg p-3 text-center hover:shadow-md transition-shadow"
              >
                <div className="text-xs font-medium text-slate-400 mb-2">
                  {formatDate(day.date)}
                </div>
                <div className="flex justify-center mb-2">
                  {getWeatherIcon(day.icon)}
                </div>
                <div className="text-sm font-bold text-white">
                  {celsiusToFahrenheit(day.temp_max)}°
                </div>
                <div className="text-xs text-slate-500">
                  {celsiusToFahrenheit(day.temp_min)}°
                </div>
                <div className="flex items-center justify-center mt-2 space-x-1">
                  <Droplets className="w-3 h-3 text-blue-400" />
                  <span className="text-xs text-slate-400">{day.precipitation_prob}%</span>
                </div>
                <div className="flex items-center justify-center mt-1 space-x-1">
                  <Wind className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-slate-400">{day.wind_speed} m/s</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Helper Text */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-800">
            <strong>Plan your day:</strong> Check precipitation and wind speed before heading to job sites. Stay safe!
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default WeatherWidget;
